import assert from 'assert';
import Backbone from 'backbone';
import {BaseType} from 'd3';
import * as d3 from 'd3';
import {ScaleLinear} from 'd3';
import $ from 'jquery';
import isEqual from 'react-fast-compare';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Side} from 'constants/MolbioConstants';
import * as context from 'Context';
// @ts-expect-error
import CircularAnnotationsView from 'molbio/maps/circular/AnnotationsView';
import CircularAxisView from 'molbio/maps/circular/AxisView';
import {CircularLayout, LabelLayout, RingLayout} from 'molbio/maps/circular/CircularLayout';
import Display from 'molbio/maps/circular/Display';
import CircularDNAView from 'molbio/maps/circular/DNAView';
import HomologyRegionAxisView from 'molbio/maps/circular/HomologyRegionAxisView';
import HomologyRegionDNAView from 'molbio/maps/circular/HomologyRegionDNAView';
import LabelsView from 'molbio/maps/circular/LabelsView';
import CircularORFsView from 'molbio/maps/circular/ORFsView';
import PartsView from 'molbio/maps/circular/PartsView';
// @ts-expect-error
import CircularPrimersView from 'molbio/maps/circular/PrimersView';
import {RingComponentOptions} from 'molbio/maps/circular/RingComponent';
import {RingView} from 'molbio/maps/circular/RingView';
import SearchResultsView from 'molbio/maps/circular/SearchResultsView';
import SegmentsRingView, {SegmentsRingData} from 'molbio/maps/circular/SegmentsRingView';
import CircularSequencePartAnnotationsView from 'molbio/maps/circular/SequencePartAnnotationsView';
import CircularTranslationsView from 'molbio/maps/circular/TranslationsView';
import {CircularMapState} from 'molbio/maps/MapStates';
import Type from 'molbio/maps/Type';
import {BaseSelectionMethod, trackSequenceMapBaseSelection} from 'molbio/SequenceAnalyticsHelper';
import * as sequtil from 'molbio/SeqUtil';
import CircularMapTabState from 'molbio/tabstates/CircularMapTabState';
import * as Perf from 'performance/Perf';
import {getAngles} from 'util/geometry/GeometryUtil';
import {ElementMouseEvent} from 'util/Types';

export interface CircularMapOptions {
  model: CircularMapState;
  tabstate: CircularMapTabState;
  renderSelection?: boolean;
}

export default class CircularMapView extends Backbone.View<CircularMapState> {
  mapstate!: CircularMapState;
  tabstate!: CircularMapTabState;
  layout!: CircularLayout;
  renderSelection!: boolean;
  angle!: ScaleLinear<number, number>;
  annotationsView!: CircularAnnotationsView;
  translationsView!: CircularTranslationsView;
  primersView!: CircularPrimersView;
  labelsView!: LabelsView;
  axisView!: CircularAxisView;
  homologyAxisView!: HomologyRegionAxisView;
  dnaView!: CircularDNAView;
  homologyDNAView!: HomologyRegionDNAView;
  searchView!: SearchResultsView;
  orfsView!: CircularORFsView;
  partsView!: PartsView;
  partAnnotationsView!: CircularSequencePartAnnotationsView;
  views!: {[view: string]: RingView<RingLayout>};
  zStackBeforeSelection!: Array<RingView<RingLayout>>;
  zStackAfterSelection!: Array<RingView<RingLayout>>;
  orderedInnerRingViews!: Array<SegmentsRingView<RingLayout, SegmentsRingData>>;
  isNearFull!: boolean;
  isActiveSelection!: boolean;
  type!: Type;
  maxLayer!: number;
  mapLoadPerfId!: Perf.PerfId;

  static initClass(): void {
    this.prototype.className = 'circular-map-wrapper';
  }

  initialize(options: CircularMapOptions): void {
    this.mapLoadPerfId = Perf.start('loadCircularMap', {enabled: false});
    this.mapstate = this.model; // for convenience
    this.tabstate = options.tabstate;
    this.layout = this.mapstate.get('layout');
    this.renderSelection = options.renderSelection ?? true;
    const offset = this.mapstate.get('DNAOffset');
    const length = this.mapstate.get('sequence').length();

    this.angle = d3
      .scaleLinear()
      .range([0, Math.PI * 2])
      .domain([offset, offset + length]);

    this.annotationsView = new CircularAnnotationsView({
      circularView: this,
      circularLayout: this.layout,
      layout: this.layout.CircularAnnotationsLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    });
    const translationsOptions: RingComponentOptions<RingLayout> = {
      circularView: this,
      layout: this.layout.CircularTranslationsLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.translationsView = new CircularTranslationsView(translationsOptions);
    this.primersView = new CircularPrimersView({
      circularView: this,
      circularLayout: this.layout,
      layout: this.layout.CircularPrimersLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    });
    const labelsOptions: RingComponentOptions<LabelLayout> = {
      circularView: this,
      circularLayout: this.layout,
      layout: this.layout.LabelLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.labelsView = new LabelsView(labelsOptions);
    const axisOptions: RingComponentOptions<RingLayout> = {
      circularView: this,
      layout: this.layout.AxisLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.axisView = new CircularAxisView(axisOptions);
    const homologyAxisOptions: RingComponentOptions<RingLayout> = {
      circularView: this,
      layout: this.layout.HomologyAxisLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.homologyAxisView = new HomologyRegionAxisView(homologyAxisOptions);
    const dnaOptions: RingComponentOptions<RingLayout> = {
      circularView: this,
      layout: this.layout.DNALayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.dnaView = new CircularDNAView(dnaOptions);
    const homologyDNAOptions = {
      circularView: this,
      circularLayout: this.layout,
      layout: this.layout.HomologyDNALayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.homologyDNAView = new HomologyRegionDNAView(homologyDNAOptions);
    const searchOptions: RingComponentOptions<RingLayout> = {
      circularView: this,
      layout: this.layout.SearchLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.searchView = new SearchResultsView(searchOptions);
    this.orfsView = new CircularORFsView({
      circularView: this,
      circularLayout: this.layout,
      layout: this.layout.CircularORFsLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    } as RingComponentOptions<RingLayout>);
    const partsOptions: RingComponentOptions<RingLayout> = {
      circularView: this,
      layout: this.layout.PartsLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.partsView = new PartsView(partsOptions);
    const partAnnotationsOptions: RingComponentOptions<RingLayout> = {
      circularView: this,
      layout: this.layout.CircularPartAnnotationsLayout,
      angle: this.angle,
      model: this.mapstate,
      tabstate: this.tabstate,
    };
    this.partAnnotationsView = new CircularSequencePartAnnotationsView(partAnnotationsOptions);

    this.views = {
      annotationsView: this.annotationsView,
      translationsView: this.translationsView,
      primersView: this.primersView,
      labelsView: this.labelsView,
      axisView: this.axisView,
      homologyAxisView: this.homologyAxisView,
      dnaView: this.dnaView,
      homologyDNAView: this.homologyDNAView,
      searchView: this.searchView,
      orfsView: this.orfsView,
      // @ts-expect-error We helpfully do something custom with the part overlay here instead of making it a
      // Selectable
      partsView: this.partsView,
      partAnnotationsView: this.partAnnotationsView,
    };

    this.zStackBeforeSelection = [
      // @ts-expect-error We helpfully do something custom with the part overlay here instead of making it a
      // Selectable
      this.partsView,
      this.dnaView,
      this.homologyDNAView,
      this.axisView,
      this.homologyAxisView,
    ];
    this.zStackAfterSelection = [
      this.searchView,
      this.partAnnotationsView,
      this.orfsView,
      this.annotationsView,
      this.translationsView,
      this.primersView,
      this.labelsView,
    ];
    this.orderedInnerRingViews = [
      this.partAnnotationsView,
      this.orfsView,
      this.primersView,
      this.translationsView,
      this.annotationsView,
    ];

    this.isNearFull = false;
    this.isActiveSelection = false;
    this.type = new Type(15);
    this.maxLayer = 0;
  }

  handleResize(): void {
    debounce(() => {
      const invalidated = this.reposition();
      if (invalidated) {
        this.update(false, false);
      } else {
        this.updateLabel();
      }
    }, 5)();
  }

  getSVGNode(): HTMLElement {
    return this.$el.children('svg')[0];
  }

  recalculateZooms(): void {
    this.computeMinZoom(true);
    this.computeMaxZoom(true);
    this.zoomToFit();
  }

  render(): this {
    Perf.markStart(this.mapLoadPerfId);
    let el;
    super.render();
    this.registerListeners();

    const svg = d3
      .select(this.el)
      .append('svg')
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
      .classed('circular-map', true);

    const container = svg.append('svg:g').classed('container', true);

    if (this.renderSelection) {
      this.makeSelection();
    }

    const text = container.append('svg:text').classed('description', true).attr('text-anchor', 'middle');
    text.append('svg:tspan').classed('title-desc', true).attr('font-weight', 900).attr('font-size', 20);

    text.append('svg:tspan').classed('length-desc', true).attr('font-size', 14).attr('x', 0).attr('dy', 20);

    for (const view of this.zStackBeforeSelection) {
      el = container.append('g').classed(view.className, true);
      view.setElement(el.node());
      view.render();
    }

    if (this.renderSelection) {
      this.makeFrontSelection();
    }

    for (const view of this.zStackAfterSelection) {
      el = container.append('g').classed(view.className, true);
      view.setElement(el.node());
      view.render();
    }

    // This seems intended to be run at initialization time (isDefault defaults to true but
    // then we set it false and never update it). Why does this happen here and not in initalize()?
    if (this.mapstate.get('isDefault')) {
      this.recalculateZooms();
      this.mapstate.set('isDefault', false);
    }

    this.update();
    this.labelsView.fastUpdate();
    Perf.markEnd(this.mapLoadPerfId);
    return this;
  }

  zoomToFit(): void {
    const width = this.mapstate.get('width')!;
    const height = this.mapstate.get('height')!;
    const radius = 0.5 * Math.min(width, height) - 100;
    this.mapstate.setZoom(this.mapstate.radiusToZoom(radius));
  }

  die(): void {
    this.stopListening();
    d3.select(window).on('.circular', null);
    d3.select(this.el).on('.circular', null);
    d3.select(this.el).select('svg').select('g').selectAll('.select-handle').on('.circular', null);
    Object.values(this.views).forEach((view) => {
      view.die();
    });
  }

  remove(): this {
    this.die();
    super.remove();
    return this;
  }

  getAngles(start: number, end: number | null): {startAngle: number; endAngle: number} {
    return getAngles(start, end, this.mapstate.get('sequence').length(), this.angle);
  }

  getHoverBase(e: Event, round: boolean = true): number {
    let targetBP;
    const length = this.mapstate.get('sequence').length();
    const [mx, my] = d3.pointer(e, d3.select(this.el).select('.container').node());
    const rads = (-(Math.atan2(-my, mx) - Math.PI / 2) + 2 * Math.PI) % (2 * Math.PI);

    if (length === 0) {
      targetBP = 0;
    } else {
      if (round) {
        targetBP = Math.round(this.angle.invert(rads)) % length;
      } else {
        targetBP = Math.floor(this.angle.invert(rads)) % length;
      }
      if (targetBP < 0) {
        targetBP += length;
      }
    }

    return Math.max(0, Math.min(length, targetBP));
  }

  goToBase(base: number, endBase: number): void {
    if (!this.mapstate.get('fullCircleView') && context.get('mapId') !== this.mapstate.cid) {
      const startBase = this.mapstate.get('DNAOffset');

      const target1 = base - this.mapstate.get('sequence').length();
      const target2 = base + this.mapstate.get('sequence').length();
      const target = _.min([base, target1, target2], (t) => Math.abs(startBase - t));

      const [leftBP, rightBP] = [this.mapstate.get('leftBP'), this.mapstate.get('rightBP')];
      if (endBase != null) {
        if (sequtil.isRangeWithin(base, endBase, leftBP, rightBP)) {
          return;
        }
      } else if (!sequtil.baseInRange(base, leftBP, rightBP)) {
        return;
      }

      $({base: startBase}).animate(
        {base: target},
        {
          duration: Math.min(1000, Math.abs(target - this.mapstate.get('DNAOffset')) * 10),
          step: ((mapstate) =>
            function () {
              // @ts-expect-error Not sure where this.base comes from.
              mapstate.set('DNAOffset', this.base);
            })(this.mapstate),
        }
      );
    }
  }

  updateAngle(): void {
    if (this.mapstate.get('fullCircleView')) {
      this.updateSameRadius();
    } else {
      this.partAnnotationsView.invalidateData();
      this.annotationsView.invalidateData();
      this.translationsView.invalidateData();
      this.update(false, false);
    }
  }

  updateAngleDomain(): ScaleLinear<number, number> {
    const offset = this.mapstate.get('DNAOffset');
    const length = this.mapstate.get('sequence').length();
    return this.angle.domain([offset, offset + length]);
  }

  updateLength(): void {
    const length = this.mapstate.get('sequence').length();

    if (length < 2) {
      this.mapstate.setOffset(0);
      this.zoomToFit();
    } else {
      this.computeMinZoom(true);
      this.computeMaxZoom(false);
    }

    this.updateLabel();
    this.update(true, false);
  }

  updateSameRadius(): void {
    if (!this.mapstate.get('fullCircleView')) {
      this.reposition();
    }
    Object.values(this.views).forEach((view) => {
      view.update();
    });
    this.updateLabel();
    this.showSelection();
  }

  getVisibleAxisView(): CircularAxisView | null {
    if (this.axisView.layout.get('visible')) {
      return this.axisView;
    } else if (this.homologyAxisView.layout.get('visible')) {
      return this.homologyAxisView;
    } else {
      return null;
    }
  }

  getDNAView(): CircularDNAView | HomologyRegionDNAView {
    if (this.dnaView.layout.get('visible')) {
      return this.dnaView;
    } else {
      return this.homologyDNAView;
    }
  }

  update(fastUpdate?: boolean, recomputeZoom: boolean = true): void {
    if (recomputeZoom) {
      this.computeMinZoom();
    }
    this.reposition(); // Changes mode and repositions.
    const mapOuterRadius = this.mapstate.getRadius();

    this.getDNAView().setOuterRadius(mapOuterRadius);

    // We will always have at most one of axisView and homologyAxisView visible, but it is valid for neither
    // of them to be visible.
    const visibleAxisView = this.getVisibleAxisView();
    // We still need to set the inner radius of both axis views so that the actual $els hide and show
    // themselves appropriately based on the layout visibility value.
    const dnaInnerRadius = this.getDNAView().getInnerRadius();
    // setOuterRadius will, if axisView and/or homologyAxisView are supposed to be hidden, set their inner
    // radius to their outer radius.
    this.axisView.setOuterRadius(dnaInnerRadius);
    this.homologyAxisView.setOuterRadius(dnaInnerRadius);
    // If both are hidden, the innermost ring view should then be based on the inner radius of the DNA view.
    const axisInnerRadius = visibleAxisView != null ? visibleAxisView.getInnerRadius() : dnaInnerRadius;

    const outermostRingViewRadius =
      axisInnerRadius - this.getDNAView().layout.vMargin() - this.layout.CircularPrimersLayout.vMargin();
    this.searchView.setInnerRadius(this.getDNAView().getInnerRadius());
    this.partsView.setInnerRadius(this.getDNAView().getInnerRadius());

    for (const [index, view] of this.orderedInnerRingViews.entries()) {
      const viewOuterRadius =
        index === 0 ? outermostRingViewRadius : this.orderedInnerRingViews[index - 1].getInnerRadius();
      view.setConstraints(viewOuterRadius);
    }
    this.labelsView.setInnerRadius(mapOuterRadius, fastUpdate);

    this.updateLabel();
    if (this.renderSelection) {
      this.showSelection();
    }
  }

  reposition(): boolean {
    // Returns true if we ought to do a update, since leftBP/rightBP/fullCircleView has changed.
    const outerRadius = this.mapstate.getRadius();
    // If we resized too large, change mode.
    const width = this.mapstate.get('width')!;
    const height = this.mapstate.get('height')!;
    const radius = Math.min(width, height) / 2;

    const getRenderData = (): [boolean, number, number] => [
      this.mapstate.get('fullCircleView'),
      this.mapstate.get('leftBP'),
      this.mapstate.get('rightBP'),
    ];
    const oldData = getRenderData();

    if (radius < outerRadius) {
      this.mapstate.set('fullCircleView', false);
    } else {
      this.mapstate.set('fullCircleView', true);
    }

    const length = this.mapstate.get('sequence').length();
    const svg = d3.select(this.el).select('svg');
    const root = svg.select('g');

    svg.attr('height', height);
    svg.attr('width', width);

    if (this.mapstate.get('fullCircleView')) {
      this.mapstate.set('cx', width / 2);
      this.mapstate.set('cy', height / 2);

      root.attr('transform', `translate(${width / 2},${height / 2})`);

      this.mapstate.set('leftBP', 0);
      this.mapstate.set('rightBP', length);
    } else {
      this.mapstate.set('cx', width / 2);
      this.mapstate.set('cy', height / 2 + outerRadius);

      const maxX = Math.min(width / 2, outerRadius);
      const y = -Math.sqrt(outerRadius * outerRadius - maxX * maxX);
      let [mx, my] = [-width / 2, y];
      let rads = (-(Math.atan2(-my, mx) - Math.PI / 2) + 2 * Math.PI) % (2 * Math.PI);
      const leftBP = ((Math.round(this.angle.invert(rads)) % length) + length) % length;

      [mx, my] = [width / 2, y];
      rads = (-(Math.atan2(-my, mx) - Math.PI / 2) + 2 * Math.PI) % (2 * Math.PI);
      const rightBP = ((Math.round(this.angle.invert(rads)) % length) + length) % length;

      this.mapstate.set('leftBP', leftBP);
      this.mapstate.set('rightBP', rightBP);
      root.attr('transform', `translate(${width / 2},${height / 2 + outerRadius})`);
    }

    const newData = getRenderData();
    return !isEqual(oldData, newData);
  }

  computeMaxZoom(silent: boolean = false): void {
    let maxRadius = (this.mapstate.get('sequence').length() * 16) / (2 * Math.PI);
    maxRadius = Math.min(10000, maxRadius);
    this.mapstate.setMaxRadius(maxRadius, silent);
  }

  computeMinZoom(silent: boolean = false): void {
    // Here we determine which annotations/primers will have labels.

    // Criterion: at the minzoom, the plasmid should be entirely visible.

    // Strategy: If all labels are still entirely visible, good. Otherwise, no labels.
    // (TODO: we could binary search to figure out a subset that do have labels.)
    const minRadius = (): number => {
      const innerRadius1 = this.mapstate.get('minInnerRadius');
      const primersViewIndex = this.orderedInnerRingViews.indexOf(this.primersView);
      const orderedMinRings = this.orderedInnerRingViews.map((view) => view.computeMinRings());
      const rings = orderedMinRings.reduce((acc, viewMinRings) => acc + viewMinRings);
      const afterPrimersRings = orderedMinRings
        .slice(primersViewIndex + 1)
        .reduce((acc, viewMinRings) => acc + viewMinRings);
      const thickness = this.layout.CircularAnnotationsLayout.get('minRingHeight');
      const innerRadius2 = innerRadius1 + rings * thickness;

      this.mapstate.set('primerCutoffRadius', innerRadius1 + afterPrimersRings * thickness);
      return (
        this.getDNAView().computeOuterRadius(innerRadius2) +
        this.getDNAView().layout.vMargin() +
        this.layout.CircularPrimersLayout.vMargin()
      );
    };

    const plasmidMinRadius = minRadius();
    this.mapstate.setMinRadius(plasmidMinRadius, silent);
  }

  changeBases(): void {
    this.invalidateLabels();
    this.partsView.invalidateData();
    this.computeMaxZoom();
    this.updateAngleDomain();
    this.updateLength();
  }

  registerListeners(): void {
    this.listenTo(this.mapstate, 'change:fullCircleView', () => {
      this.invalidateLabels();
    });
    this.listenTo(this.mapstate, 'change:DNAOffset', () => {
      this.updateAngleDomain();
      this.updateAngle();
    });
    this.listenTo(this.mapstate.get('sequence').get('_sequence'), 'change:bases', () => {
      this.changeBases();
    });
    this.listenTo(this.mapstate.get('sequence').get('_sequence'), 'change:parts', () => {
      this.changeBases();
    });

    this.listenTo(this.mapstate.get('sequence'), 'jump', (start: number, end: number) => {
      this.goToBase(start, end);
    });
    Object.values(this.views).forEach((view) => {
      view.registerListeners();
    });

    this.listenTo(this.mapstate, 'redraw', () => {
      this.update(true);
    });
    this.listenTo(this.mapstate, 'change:zoom', () => {
      if (this.mapstate.get('fullCircleView')) {
        this.invalidateLayers(); // somethings might need to be relayered, but the data should be fine.
      } else {
        this.invalidateLabels(); // leftBP and rightBP might change, which means we need to recompute what's visible.
      }

      const selection = this.mapstate.get('sequence').get('selection');
      let [leftBP, rightBP] = [this.mapstate.get('leftBP'), this.mapstate.get('rightBP')];
      const [startBP, endBP] = [selection.start(), selection.end() ?? selection.start()];

      const wasVisible = sequtil.isRangeOverlap(startBP, endBP, leftBP, rightBP);
      this.update(false, false);

      [leftBP, rightBP] = [this.mapstate.get('leftBP'), this.mapstate.get('rightBP')];

      if (!this.mapstate.get('fullCircleView') && wasVisible) {
        let bpIncrement;
        const leftIncrement = startBP - leftBP;
        if (!sequtil.baseInRange(startBP, leftBP, rightBP)) {
          bpIncrement = leftIncrement;
        } else if (!sequtil.baseInRange(endBP, leftBP, rightBP)) {
          bpIncrement = sequtil.baseInRange(startBP, leftBP + (endBP - rightBP), endBP)
            ? endBP - rightBP
            : leftIncrement;
        } else {
          bpIncrement = 0;
        }
        this.mapstate.setOffset(this.mapstate.get('DNAOffset') + bpIncrement);
      }
    });

    this.listenTo(this.mapstate, 'invalidateLayers', () => {
      this.invalidateLayers();
    });
    this.listenTo(this.mapstate.get('sequence').get('_sequence'), 'change:name', () => {
      this.updateLabel();
    });
    if (this.renderSelection) {
      this.listenTo(this.mapstate.get('sequence').get('selection'), 'change', () => {
        this.showSelection();
      });
    }

    this.listenTo(this.mapstate, 'change:width change:height', () => {
      this.handleResize();
    });

    d3.select(this.el)
      .on('mousedown.circular', (e) => {
        this.updateSelection(e);
      })
      .on('mousemove.circular', (e) => {
        this.updateSelection(e);
      })
      .on('mouseup.circular', (e) => {
        this.updateSelection(e);
      })
      .on('dblclick.circular', (e) => {
        this.updateSelection(e);
      })
      .on('contextmenu.circular', (e, d) => {
        if ((e as ElementMouseEvent).altKey && window.debug) {
          return;
        }
        const selection = this.mapstate.get('sequence').get('selection');
        const base = this.getHoverBase(e);
        selection.reset(base);
      });

    d3.select(window).on('mouseup.circular', (e) => {
      this.updateSelection(e);
    });
  }

  updateSelection(e: Event, type?: string): void {
    const hoveredBaseIndex = this.getHoverBase(e);
    const selection = this.mapstate.get('sequence').get('selection');
    type = type ?? e.type;

    switch (type) {
      case 'mousemove':
        if (selection.get('active') === 'circular') {
          selection.circularExtend(hoveredBaseIndex);
        }
        break;
      case 'mousedown':
        assert(e instanceof MouseEvent);
        e.preventDefault();
        this.$el.closest('.buffer-tab').focus();
        if (e.shiftKey) {
          trackSequenceMapBaseSelection(
            this.model.get('sequence'),
            BaseSelectionMethod.shiftClickDown,
            selection.start(),
            selection.end(),
            'circular'
          );
          selection.expand(hoveredBaseIndex, false);
          selection.set({active: 'circular'});
          e.stopPropagation();
        } else if (e.which !== 3 && !e.ctrlKey) {
          selection.reset(hoveredBaseIndex);
          selection.set({active: 'circular'});
          e.stopPropagation();
        }
        break;
      case 'mouseup':
        assert(e instanceof MouseEvent);
        if (selection.get('active') === 'circular' && e.which !== 3 && !e.ctrlKey) {
          selection.circularExtend(hoveredBaseIndex);
          const selectionMethod = e.shiftKey
            ? BaseSelectionMethod.shiftClickUp
            : BaseSelectionMethod.clickAndDrag;
          trackSequenceMapBaseSelection(
            this.model.get('sequence'),
            selectionMethod,
            selection.start(),
            selection.end(),
            'circular'
          );
          selection.set({active: null});
        }
        break;
      case 'dblclick':
        this.model.get('sequence').handleDoubleClick(hoveredBaseIndex, hoveredBaseIndex);
        trackSequenceMapBaseSelection(
          this.model.get('sequence'),
          BaseSelectionMethod.doubleClick,
          selection.start(),
          selection.end(),
          'circular'
        );
        e.stopPropagation();
        break;
      default:
        throw Error('Unexpected selection update');
    }
  }

  invalidateLabels(): void {
    this.partAnnotationsView.invalidateData();
    this.annotationsView.invalidateData();
    this.translationsView.invalidateData();
    this.primersView.invalidateData();
  }

  invalidateLayers(): void {
    this.partAnnotationsView.invalidateLayers();
    this.annotationsView.invalidateLayers();
    this.translationsView.invalidateLayers();
    this.primersView.invalidateLayers();
  }

  makeSelectHandle(
    selector: d3.Selection<BaseType, unknown, BaseType, unknown>,
    type: Side
  ): d3.Selection<BaseType, unknown, BaseType, unknown> {
    return selector.on('mousedown.circular', (e, d) => {
      e.stopPropagation();
      this.$el.closest('.buffer-tab').focus();
      const selection = this.mapstate.get('sequence').get('selection');
      selection.set('active', 'circular');
      return selection.setFreeEnd(type);
    });
  }

  makeFrontSelection(): d3.Selection<BaseType, unknown, BaseType, unknown> {
    const svg = d3.select(this.el).select('svg');
    const root = svg.select('g');

    return root
      .append('path')
      .classed('selection-hidden', true)
      .on('contextmenu', (e, d) => {
        const choices = selectioncontextmenu.getSelectionMenu();
        return selectioncontextmenu.open(e, this.mapstate.get('sequence'), choices);
      });
  }

  makeSelection(): d3.Selection<BaseType, unknown, BaseType, unknown> {
    const svg = d3.select(this.el).select('svg');
    const root = svg.select('g');
    const selector = root.append('g').classed('selection-comp', true);

    selector.append('path').classed('selection', true);

    selector
      .append('path')
      .classed('select-handle', true)
      .classed('start-select', true)
      .classed('selection-line', true)
      .call((s, t) => this.makeSelectHandle(s, t), 'start');

    selector
      .append('path')
      .classed('select-handle', true)
      .classed('end-select', true)
      .classed('selection-line', true)
      .call((s, t) => this.makeSelectHandle(s, t), 'end');

    selector
      .append('polygon')
      .classed('select-handle', true)
      .classed('start-cursor-triangle', true)
      .call((s, t) => this.makeSelectHandle(s, t), 'start');

    selector
      .append('polygon')
      .classed('select-handle', true)
      .classed('end-cursor-triangle', true)
      .call((s, t) => this.makeSelectHandle(s, t), 'end');

    return selector.append('path').classed('map-cursor-line', true).classed('line-blink', true);
  }

  showSelection(): void {
    const sequence = this.mapstate.get('sequence');
    const selection = sequence.get('selection');

    const svg = d3.select(this.el).select('svg');
    const root = svg.select('g');

    const start = selection.start();
    const end = selection.end();

    const selector = root.select('.selection-comp');
    const frontSelector = root.select('.selection-hidden');
    if (start == null || this.mapstate.get('sequence').length() < 1) {
      selector.attr('visibility', 'hidden');
      frontSelector.attr('visibility', 'hidden');
      return;
    }

    selector.attr('visibility', 'visible');
    frontSelector.attr('visibility', 'visible');

    const angles = this.getAngles(start, end);

    // YOLO TODO FIXME for some reason, @views.annotationsView.getInnerRadius() can be really negative after
    // inserting a part with lots of bases... the only thing that is rendering incorrectly is
    // the selection..
    const innerR = Math.max(
      this.mapstate.get('minInnerRadius'),
      this.views.annotationsView.getInnerRadius() - 5
    );
    const outerR = this.getDNAView().getOuterRadius() + 8;

    selector
      .selectAll('.selection')
      .classed('can-pcr', selection.canPCR())
      .classed('cant-edit', !sequence.allowEdits() || !sequence.isSelectionDeletable())
      .attr('visibility', 'inherit')
      .attr(
        'd',
        d3
          .arc()
          .innerRadius(innerR)
          .outerRadius(outerR)
          .startAngle(angles.startAngle)
          .endAngle(angles.endAngle)
      );

    d3.select(this.el)
      .select('svg')
      .select('.selection-hidden')
      .attr(
        'd',
        d3
          .arc()
          .innerRadius(innerR)
          .outerRadius(outerR)
          .startAngle(angles.startAngle)
          .endAngle(angles.endAngle)
      );

    const startDeg = Display.angleToDisplayDegrees(angles.startAngle);

    const endDeg = Display.angleToDisplayDegrees(angles.endAngle);

    const triangleWidth = this.layout.SelectionLayout.get('triangleWidth');

    const showCursor = end == null;
    selector
      .selectAll('.map-cursor-line')
      .attr('d', `M 0 ${-innerR} L ${0} ${-outerR}`)
      .attr('transform', `rotate(${startDeg})`)
      .classed('hide', !showCursor);
    selector
      .selectAll('.end-select')
      .attr('d', `M 0 ${-innerR} L ${0} ${-outerR}`)
      .attr('transform', `rotate(${endDeg})`)
      .classed('hide', showCursor);
    selector
      .selectAll('.start-select')
      .attr('d', `M 0 ${-innerR} L ${0} ${-outerR}`)
      .attr('transform', `rotate(${startDeg})`)
      .classed('hide', showCursor);
    selector
      .selectAll('.start-cursor-triangle')
      .attr(
        'points',
        `${-triangleWidth},${-outerR}, 0,${-outerR + triangleWidth} ${triangleWidth},${-outerR}`
      )
      .attr('transform', `rotate(${startDeg})`)
      .classed('hide', showCursor);
    selector
      .selectAll('.end-cursor-triangle')
      .attr(
        'points',
        `${-triangleWidth},${-outerR}, 0,${-outerR + triangleWidth} ${triangleWidth},${-outerR}`
      )
      .attr('transform', `rotate(${endDeg})`)
      .classed('hide', showCursor);
  }

  updateLabel(): void {
    const svg = d3.select(this.el).select('svg');
    const root = svg.select('g');

    let name = this.mapstate.get('sequence').get('_sequence').displayNames();

    const titleLayout = this.layout.TitleLayout;
    const minFontSize = titleLayout.get('minFontSize');
    const maxFontSize = titleLayout.get('maxFontSize');

    let targetFontSize = Type.fitToWidth(name.length, this.views.annotationsView.getInnerRadius() * 2)!;

    root.select('.length-desc').text(`${this.mapstate.get('sequence').length()} bp`);
    // Try displaying inside the plasmid.
    if (targetFontSize >= minFontSize && this.mapstate.get('fullCircleView')) {
      titleLayout.get('type').setFontSize(Math.min(maxFontSize, targetFontSize));
      root
        .select('.title-desc')
        .text(name)
        .attr('font-size', titleLayout.get('type').fontSize)
        .attr('visibility', 'visible')
        .attr('y', 0);
      root
        .select('.length-desc')
        .attr('font-size', (titleLayout.get('type').fontSize * 14) / 20)
        .attr('dy', root.select('.title-desc').attr('font-size'))
        .attr('visibility', 'visible');
      return;
    }

    // Try displaying on top of the plasmid.
    const topY = -this.mapstate.get('cy')! + titleLayout.get('type').height() + 10;
    const width = this.mapstate.get('width')! - 20; // padding
    targetFontSize = Type.fitToWidth(name.length, width)!;

    const cY = this.mapstate.get('cy')!;

    // Try to position the name 50 pixels above the outer radius, rounded to nearest
    // multiple of 10 to ensure it doesn't 'fidget'

    const targetY = Math.floor((-this.labelsView.labelRadius() - 50 + cY) / 10) * 10 - cY;

    if (targetY > topY) {
      if (targetFontSize < minFontSize) {
        titleLayout.get('type').setFontSize(minFontSize);
        targetFontSize = minFontSize;
        name = `${name.slice(0, titleLayout.get('type').numChars(width) - 3)}...`;
      }
      titleLayout.get('type').setFontSize(Math.min(maxFontSize, targetFontSize));

      root
        .select('.length-desc')
        .attr('font-size', Math.max(minFontSize, (titleLayout.get('type').fontSize * 14) / 20))
        .attr('visibility', 'visible');
      root
        .select('.title-desc')
        .text(name)
        .attr('font-size', titleLayout.get('type').fontSize)
        .attr('visibility', 'visible')
        .attr('y', targetY);

      return;
    }
    // Otherwise, it's too big, so let's ellipsize on top.
    // Don't display
    root.select('.title-desc').attr('visibility', 'hidden');
    root.select('.length-desc').attr('visibility', 'hidden');
  }
}

CircularMapView.initClass();
